import { InvoiceAllocationRuleRuleType, InvoiceTaggingRule, InvoiceTaggingRuleRuleType } from '@apis/Invoices/model';
import { InvoiceAllocationRule } from '@apis/Invoices/model/invoiceAllocationRule';
import { Group, Sx, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import { Box, UnstyledButton, Image } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { useNav } from '@root/Services/NavigationService';
import { useDiMemo } from '@root/Services/DI';
import { AllocationRuleEditorContext } from './Model';
import { Platform } from '@root/Services/PlatformService';

const typeDescriptorLookup: Record<InvoiceAllocationRuleRuleType | InvoiceTaggingRuleRuleType, { image: string; ruleName: string }> = {
    UntaggedCosts: { image: 'untaggedCosts', ruleName: 'Untagged Costs' },
    SharedCosts: { image: 'sharedCosts', ruleName: 'Shared Costs' },
    Discounts: { image: 'discounts', ruleName: 'Discounts' },
    Marketplace: { image: 'marketplace', ruleName: 'Marketplace' },
    Advanced: { image: 'advanced', ruleName: 'Advanced' },
};

const getImage = (type: InvoiceAllocationRuleRuleType, state?: 'selected' | 'hover') => {
    const imgType = typeDescriptorLookup[type].image;
    return `/assets/showback/ruletypes/card-${imgType}-${state == 'selected' ? 'on' : state == 'hover' ? 'hover' : 'off'}.png`;
};

export function AllocationRuleTypeCard({
    ruleType,
    selected,
    disabled,
    onClick,
}: {
    ruleType: InvoiceAllocationRuleRuleType | InvoiceTaggingRuleRuleType;
    selected: boolean;
    disabled?: boolean;
    onClick: (type: InvoiceAllocationRuleRuleType | InvoiceTaggingRuleRuleType) => void;
}) {
    const { hovered, ref } = useHover();
    const src = getImage(ruleType, selected ? 'selected' : !disabled && hovered ? 'hover' : undefined);
    const ruleName = typeDescriptorLookup[ruleType].ruleName;
    const select = useCallback(() => onClick(ruleType), [ruleType]);
    const disabledStyle: Sx = disabled && !selected ? { filter: `saturate(0.1) opacity(0.5)`, cursor: 'not-allowed' } : {};
    return (
        <Box ref={ref} sx={{ lineHeight: 0 }}>
            <UnstyledButton onClick={select} sx={{ cursor: 'pointer', ...disabledStyle }}>
                <Image src={src} alt={ruleName} height={122} />
            </UnstyledButton>
        </Box>
    );
}

export const AllocationRuleTypeList = observer(function AllocationRuleTypeList({ rule }: { rule: InvoiceAllocationRule | InvoiceTaggingRule }) {
    const model = useDiMemo(AllocationRuleEditorContext);

    const ruleTypes = useMemo(() => {
        const ruleTypes: Array<[InvoiceAllocationRuleRuleType, null | Platform]> = [
            ['UntaggedCosts', null],
            ['SharedCosts', 'Aws'],
            ['Discounts', 'Aws'],
            ['Marketplace', 'Aws'],
            ['Advanced', null],
        ];
        const availRuleTypes = ruleTypes
            .filter(([, platform]) => !platform || (model.availabilityInfo.hasAws && platform === 'Aws'))
            .map(([t]) => t) as InvoiceAllocationRuleRuleType[];
        return availRuleTypes;
    }, [model.availabilityInfo]);
    const navSvc = useNav();
    const selectType = useCallback(
        (type: InvoiceAllocationRuleRuleType) => {
            if (!rule.Id) {
                rule.RuleType = type;
                const nextParams = { ...navSvc.getParams(), ruleType: type } as Record<string, string>;
                delete nextParams.subType;
                navSvc.replaceParams(nextParams);
            }
        },
        [rule]
    );
    return !model && !ruleTypes ? null : (
        <>
            <Group p="md" spacing="md" sx={{ margin: '0px 8px' }}>
                {ruleTypes.map((t) => (
                    <AllocationRuleTypeCard key={t} selected={rule.RuleType === t} ruleType={t} disabled={!!rule.Id} onClick={selectType} />
                ))}
            </Group>
        </>
    );
});
